import moment from 'moment';
import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'recompose';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  DeleteConfirmModal,
  DuplicateConfirmModal,
} from 'components/FormModal';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import ThemedSelect from 'components/ThemedSelect';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { useGetAllAccounts } from 'pages/app/Credits/gql/queries/useGetAllAccounts';
import DeleteAnnualTicket from '../AddAnnualTickets/gql/DeleteAnnualTicket';
import { useDuplicateAnnual } from '../AddAnnualTickets/gql/DuplicateAnnualTicket';
import { useGetAllAnnualTickets } from '../gql/UseGetAllAnnualTickets';
import {
  PageWrapper,
  Container,
  DashboardLayout,
  MainContent,
  Sidebar,
  HeaderContainer,
  HeaderTitle,
  ContentWrapper,
  PageHeader,
  FilterRow,
  FilterContainer,
  StatusBadge,
  TicketName,
  BackButton,
} from './StyledComponents';

// Back arrow icon
const BackArrowIcon = () => (
  <svg
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

// Plus icon for create button
const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 4v16m8-8H4"
    />
  </svg>
);

// Duplicate icon for action
const DuplicateIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeWidth="2" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
    />
  </svg>
);

const isTicketHoss = 'tickethoss' === process.env.REACT_APP_PLATFORM;
const annualLabel = isTicketHoss ? 'Annual Ticket' : 'Annual Pass';
const annualLabelPlural = isTicketHoss ? 'Annual Tickets' : 'Annual Passes';

const AnnualTicketsPage = (props) => {
  const { deleteAnnualTicket } = props;
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Get current year
  const currentYear = Number(moment().format('YYYY'));

  // Filter state - local instead of context for simpler management
  const [accountFilter, setAccountFilter] = useState(null);
  const [year, setYear] = useState(currentYear);
  const [annualType, setAnnualType] = useState(null);

  // Modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [ticketToDuplicate, setTicketToDuplicate] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  // Search and pagination
  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();
  const previousSearchRef = useRef(input.queryString || '');

  // Back navigation handler
  const handleBack = () => {
    // Check if coming from account hub (has account_id in URL)
    const accountId = input.account_id;
    if (accountId) {
      history.push(`${routePrefix}/accounts/${accountId}/annuals`);
    } else {
      history.goBack();
    }
  };

  // Get account_id from URL params and pre-select account filter
  const { data: accountsData } = useGetAllAccounts();

  useEffect(() => {
    if (input.account_id && accountsData?.getAllAccounts && !accountFilter) {
      const account = accountsData.getAllAccounts.find(
        (acc) => String(acc.id) === String(input.account_id)
      );
      if (account) {
        setAccountFilter({ value: account.id, label: account.business_dba });
      }
    }
  }, [input.account_id, accountsData, accountFilter, setAccountFilter]);

  // Fetch annual tickets
  const {
    res,
    loading,
    updateQuery: updateAnnualTicketsCache,
  } = useGetAllAnnualTickets(
    accountFilter ? accountFilter.value : null,
    null,
    null,
    annualType ? annualType : null,
    null,
    year ? +year : null
  );

  const duplicateAnnual = useDuplicateAnnual();

  const tickets = res?.getAllAnnualTickets?.results ?? [];
  const totalCount = res?.getAllAnnualTickets?.count ?? 0;
  const totalPages = Math.ceil(totalCount / 15);

  // Handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const handleSearch = (searchTerm) => {
    if (searchTerm !== previousSearchRef.current) {
      previousSearchRef.current = searchTerm;
      updateQuery({ queryString: searchTerm, page: 1 });
    }
  };

  const viewTicketPath = (ticket) => {
    const params = ticket.ticket?.id
      ? `?ticketId=${ticket.ticket.id}&ticketName=${encodeURIComponent(
          ticket.name || ''
        )}`
      : '';
    return `${routePrefix}/annualUsers/${
      ticket.account?.id || ''
    }/${encodeURIComponent(ticket.account?.business_dba || '')}${params}`;
  };

  const viewTransactionsPath = (ticket) =>
    `${routePrefix}/annualPurchases/purchase/${
      ticket.id
    }?ticketName=${encodeURIComponent(
      ticket.name
    )}&accountName=${encodeURIComponent(
      ticket.account?.business_dba || ''
    )}&account_id=${ticket.account?.id || ''}`;

  const editTicketPath = (ticket) =>
    `${routePrefix}/editAnnualTicket/${ticket.id}`;

  const handleDeleteClick = (ticket) => {
    setTicketToDelete(ticket);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ticketToDelete) return;

    setIsDeleting(true);
    try {
      const response = await deleteAnnualTicket(
        ticketToDelete.id,
        accountFilter?.value
      );

      if (!response || response.errors) {
        setToasterData({
          title: 'Delete Failed',
          message: 'Could not delete this Annual Ticket.',
          type: 'error',
        });
      } else {
        setToasterData({
          title: 'Annual Ticket Deleted',
          message: `"${ticketToDelete.name}" was deleted.`,
          type: 'success',
        });
      }
    } catch (error) {
      setToasterData({
        title: 'Delete Failed',
        message: 'Could not delete this Annual Ticket.',
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
      setDeleteModalVisible(false);
      setTicketToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setTicketToDelete(null);
  };

  const handleDuplicateTicket = (ticket) => {
    setTicketToDuplicate(ticket);
    setDuplicateModalVisible(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!ticketToDuplicate) return;

    setIsDuplicating(true);
    try {
      const response = await duplicateAnnual(ticketToDuplicate.id);
      const newAnnual = response?.data?.duplicateAnnual;
      if (!newAnnual || response?.errors) {
        setToasterData({
          title: 'Duplicate Failed',
          message: 'Could not duplicate this Annual Ticket.',
          type: 'error',
        });
      } else {
        updateAnnualTicketsCache((prev) => {
          if (!prev?.getAllAnnualTickets) return prev;
          const existing = prev.getAllAnnualTickets.results || [];
          if (existing.some((t) => t.id === newAnnual.id)) return prev;
          const sorted = [...existing, newAnnual].sort((a, b) =>
            (a.name || '')
              .toLowerCase()
              .localeCompare((b.name || '').toLowerCase())
          );
          return {
            ...prev,
            getAllAnnualTickets: {
              ...prev.getAllAnnualTickets,
              count: (prev.getAllAnnualTickets.count || 0) + 1,
              results: sorted,
            },
          };
        });
        setToasterData({
          title: 'Annual Ticket Duplicated',
          message: `"${newAnnual.name}" was created.`,
          type: 'success',
        });
      }
    } catch (error) {
      setToasterData({
        title: 'Duplicate Failed',
        message: 'Could not duplicate this Annual Ticket.',
        type: 'error',
      });
    } finally {
      setIsDuplicating(false);
      setDuplicateModalVisible(false);
      setTicketToDuplicate(null);
    }
  };

  const handleDuplicateCancel = () => {
    setDuplicateModalVisible(false);
    setTicketToDuplicate(null);
  };

  const handleCreateAnnual = () => {
    const addUrl = accountFilter
      ? `${routePrefix}/annualTickets/add?account_id=${accountFilter.value}`
      : `${routePrefix}/annualTickets/add`;
    history.push(addUrl);
  };

  // Table columns
  const columns = [
    {
      field: 'track',
      header: 'Track / Series',
      render: (ticket) => {
        const trackName =
          ticket.tracks?.[0]?.name || ticket.series?.[0]?.name || '—';
        return trackName;
      },
    },
    {
      field: 'account',
      header: 'Account',
      render: (ticket) => ticket.account?.business_dba || '—',
    },
    {
      field: 'name',
      header: `${annualLabel} Name`,
      render: (ticket) => <TicketName>{ticket.name}</TicketName>,
    },
    {
      field: 'event',
      header: 'Event',
      render: (ticket) => ticket.events?.[0]?.name || '—',
    },
    {
      field: 'offSale',
      header: 'Off Sale',
      render: (ticket) => {
        const saleEnd = ticket.ticket?.sale_end;
        return saleEnd
          ? moment(saleEnd, 'MM-DD-YYYY')
              .format('ddd MMM D - YYYY')
              .toUpperCase()
          : '—';
      },
    },
    {
      field: 'status',
      header: 'Status',
      render: (ticket) => (
        <StatusBadge status={ticket.status}>{ticket.status || '—'}</StatusBadge>
      ),
    },
  ];

  // Table actions
  const actions = [
    {
      type: 'view',
      title: 'Members',
      to: viewTicketPath,
    },
    {
      type: 'credit',
      title: 'Transactions',
      to: viewTransactionsPath,
    },
    {
      type: 'edit',
      title: 'Edit',
      to: editTicketPath,
    },
    {
      type: 'duplicate',
      title: 'Duplicate',
      icon: <DuplicateIcon />,
      onClick: handleDuplicateTicket,
    },
    {
      type: 'delete',
      title: 'Delete',
      onClick: handleDeleteClick,
    },
  ];

  // Quick actions for sidebar
  const quickActions = [
    {
      label: 'Create Annual',
      icon: <PlusIcon />,
      onClick: handleCreateAnnual,
    },
  ];

  return (
    <EnhancedThemeProvider>
      <PageWrapper>
        <Container>
          {/* Header Card */}
          <Card style={{ marginBottom: '30px' }}>
            <HeaderContainer>
              <BackButton onClick={handleBack} title="Go back">
                <BackArrowIcon />
              </BackButton>
              <PageHeaderIcon icon="events" />
              <HeaderTitle>{annualLabelPlural}</HeaderTitle>
            </HeaderContainer>
          </Card>

          {/* Dashboard Layout */}
          <DashboardLayout>
            <MainContent>
              {/* Data Table Card */}
              <Card noPadding>
                <ContentWrapper>
                  {/* Search + Filters Row */}
                  <PageHeader>
                    <SearchInput
                      onSearch={handleSearch}
                      placeholder="Search tickets..."
                    />
                    <FilterRow>
                      <FilterContainer>
                        <ThemedSelect
                          placeholder="Year"
                          options={[
                            {
                              value: currentYear - 2,
                              label: String(currentYear - 2),
                            },
                            {
                              value: currentYear - 1,
                              label: String(currentYear - 1),
                            },
                            { value: currentYear, label: String(currentYear) },
                            {
                              value: currentYear + 1,
                              label: String(currentYear + 1),
                            },
                          ]}
                          value={{ value: year, label: String(year) }}
                          onChange={(selected) =>
                            setYear(selected?.value || currentYear)
                          }
                        />
                      </FilterContainer>
                      <FilterContainer>
                        <ThemedSelect
                          placeholder="Annual Type"
                          options={[
                            { value: 'membership', label: 'Memberships' },
                            { value: 'annual', label: annualLabelPlural },
                          ]}
                          value={annualType}
                          onChange={(selected) => setAnnualType(selected)}
                          isClearable
                        />
                      </FilterContainer>
                    </FilterRow>
                  </PageHeader>

                  <DataTable
                    columns={columns}
                    data={tickets}
                    loading={loading}
                    keyField="id"
                    emptyMessage="No annual tickets found"
                    emptySubtext="Try adjusting your filters or create a new annual ticket"
                    emptyIcon={
                      <svg
                        width="48"
                        height="48"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={{ marginBottom: '16px', opacity: 0.5 }}
                      >
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                          strokeWidth="1.5"
                        />
                        <line x1="16" y1="2" x2="16" y2="6" strokeWidth="1.5" />
                        <line x1="8" y1="2" x2="8" y2="6" strokeWidth="1.5" />
                        <line
                          x1="3"
                          y1="10"
                          x2="21"
                          y2="10"
                          strokeWidth="1.5"
                        />
                      </svg>
                    }
                    actions={actions}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </ContentWrapper>
              </Card>
            </MainContent>

            <Sidebar>
              <QuickActions title="Quick Actions" actions={quickActions} />
            </Sidebar>
          </DashboardLayout>
        </Container>
      </PageWrapper>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalVisible}
        onClose={handleDeleteCancel}
        title="Delete Annual Ticket"
        message={`Are you sure you want to delete "${ticketToDelete?.name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />

      {/* Duplicate Confirmation Modal */}
      <DuplicateConfirmModal
        isOpen={duplicateModalVisible}
        onClose={handleDuplicateCancel}
        title="Duplicate Annual Ticket"
        message={`Duplicate "${ticketToDuplicate?.name}"? A copy will be created with the same settings.`}
        confirmText="Duplicate"
        cancelText="Cancel"
        onConfirm={handleDuplicateConfirm}
        isDuplicating={isDuplicating}
      />

      {toasterData && (
        <Toaster
          isOpen={!!toasterData}
          onClose={() => setToasterData(null)}
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
        />
      )}
    </EnhancedThemeProvider>
  );
};

export default compose(DeleteAnnualTicket)(AnnualTicketsPage);
